import { isSSR, type Merge } from '@sohanemon/utils';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import * as React from 'react';
import type { RouteAliasPath } from '@/lib/config/route-aliases';
import type { FileRouteTypes } from '@/routeTree.gen';

declare module '@tanstack/react-router' {
	interface HistoryState {
		transitionName?: string;
	}
}

if (!isSSR && typeof document.startViewTransition === 'function') {
	let current: ViewTransition | null = null;
	const original = document.startViewTransition.bind(document);

	document.startViewTransition = (callback) => {
		current?.skipTransition();
		current = original(callback);
		current.finished.finally(() => {
			current = null;
		});
		return current;
	};
}

// TanStack Router only types internal routes in `to` (tanstack/router#4901).
// Extend with Nitro aliases from vite.config.ts and https:// URLs; UTM params
// are added automatically for external links.
export type LinkTo =
	| FileRouteTypes['to']
	| RouteAliasPath
	| '.'
	| '..'
	| `https://${string}`;

const AnchorLink = React.forwardRef<
	HTMLAnchorElement,
	React.AnchorHTMLAttributes<HTMLAnchorElement> & { noRef?: boolean }
>(({ href, noRef, ...props }, ref) => {
	const resolvedHref =
		!noRef && href?.startsWith('https://')
			? (() => {
					const u = new URL(href);
					u.searchParams.set('utm_source', 'sohanscript');
					return u.toString();
				})()
			: href;
	return <a ref={ref} href={resolvedHref} {...props} />;
});

AnchorLink.displayName = 'AnchorLink';

const CreatedLink: LinkComponent<typeof AnchorLink> = createLink(AnchorLink);

export type LinkProps = Merge<
	React.ComponentProps<typeof CreatedLink>,
	Partial<{
		noRef: boolean;
		to: LinkTo;
		viewTransition:
			| { types: Array<'fade' | 'slide-left' | 'slide-right'> }
			| boolean;
	}>
>;

export function Link({ to, viewTransition: vt, ...props }: LinkProps) {
	const viewTransition =
		vt === true
			? { types: [] }
			: vt !== undefined
				? vt
				: to
					? { types: ['fade'] }
					: undefined;

	return (
		<CreatedLink to={to ?? '.'} viewTransition={viewTransition} {...props} />
	);
}
